'use strict';

import jQuery from 'jquery';

var VideoModel = (function($) {

  var defaultState = {
    content: {},
    updated: 0
  };

  // Define a new VideoModel function
  function VideoModel(initialState) {

    // Define the default empty state for the model
    this._state = $.extend({}, defaultState, initialState);
  }

  /**
   * Fetches a video from the server based on a show id and video post name
   *
   * @param {int} videoId The id of the video to lookup
   * @param {boolean} [force] A boolean flag to force the fetch to ask the server
   * @returns {jQuery.Promise} A promise the resolves to a fetched model
   */
  VideoModel.prototype.fetch = function fetch(videoId, force) {

    // Create a self reference for association during callbacks
    var self = this;

    // If this model has already been fetched and a force is not required, then
    // just return a promise that will resolve with the self reference
    if (this._state.updated !== 0 && (typeof force === 'undefined' || force !== true)) {

      // Simply return a resolved promise
      return $.Deferred().resolve(self).promise();
    }

    // Create a promise to fetch the core data for the video
    var contentPromise = $.ajax({
      url: '/api/v1/video/' + videoId + '/',
      type: 'GET',
      dataType: 'json'
    });

    // Chain the content promise in to the social promise and then they are
    // both resolved, provide back a reference to this model through a promise
    return contentPromise.then(function resolveShowPromise(video) {

      // Store the resulting video data
      self._state.content = video;

      // Return the content state
      return self._state.content;
    }).then(function updateFetchTimestamp() {

      // Update the timestamp for the last fetch
      self._state.updated = Date.now();
    }).then(function resolveFetchPromiseChain() {

      // Now that both results have been store, return the reference to this
      // model as the value of the next promise resolution
      return self;
    }).promise();
  };

  /**
   * Fetches the next video to play from the server based on a show id and
   * a video post name
   *
   * @param {int} showId The id of the video to lookup
   * @returns {jQuery.Promise} A promise the resolves to a fetched model
   */
  VideoModel.prototype.fetchNextVideo = function fetchNextVideo(showId) {

    // Create a promise to fetch the core data for the next video
    return $.ajax({
      url: '/api/v1/video/' + this._state.content.id + '/next/',
      type: 'GET',
      dataType: 'json',
      data: {
        postId: showId,
      }
    }).then(function mapToVideoModel(videoContent) {

      // If a non-empty object was returned then return a new model, otherwise
      // return null
      return videoContent ? new VideoModel({content: videoContent}) : null;
    }).promise();
  };

  VideoModel.prototype.fetchPrevVideo = function fetchPrevVideo(showId) {

    // Create a promise to fetch the core data for the prev video
    return $.ajax({
      url: '/api/v1/video/' + this._state.content.id + '/previous/',
      type: 'GET',
      dataType: 'json',
      data: {
        postId: showId,
      }
    }).then(function mapToVideoModel(videoContent) {

      // If a non-empty object was returned then return a new model, otherwise
      // return null
      return videoContent ? new VideoModel({content: videoContent}) : null;
    }).promise();
  };

  /**
   * Gets the video specific content from the model
   *
   * @returns {object} Returns an object of the currently fetched content
   */
  VideoModel.prototype.getContent = function getContent() {

    // Return the state
    return this._state.content;
  };

  /**
   * Sets the video specific content for the model
   *
   * @returns {object} Returns an object of the currently set content
   */
  VideoModel.prototype.setContent = function setContent(content) {

    // Set the content data
    this._state.content = content;

    // Return the state
    return this._state.content;
  };

  /**
   * Returns a JSON representation of model
   *
   * @returns {{content: Object, social: Object}}
   */
  VideoModel.prototype.toJSON = function toJSON() {

    return {
      content: this.getContent()
    };
  };

  return VideoModel;
})(jQuery);

export default VideoModel